const sessionKey = 'hotle.authSession';
const verifierKey = 'hotle.pkceVerifier';
const stateKey = 'hotle.oauthState';

interface TokenResponse {
  readonly access_token: string;
  readonly id_token?: string;
  readonly refresh_token?: string;
  readonly expires_in: number;
  readonly token_type: string;
}

export interface AuthSession {
  readonly accessToken: string;
  readonly idToken?: string;
  readonly refreshToken?: string;
  readonly expiresAt: number;
  readonly createdAt?: number;
}

export interface UserProfile {
  readonly email?: string;
  readonly name?: string;
  readonly username?: string;
  readonly groups: string[];
}

export interface AuthConfig {
  readonly clientId: string;
  readonly domain: string;
  readonly redirectSignIn: string;
  readonly redirectSignOut: string;
  readonly scopes: string[];
}

export const authConfig: AuthConfig = {
  clientId: import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID ?? '',
  domain: normalizeDomain(import.meta.env.VITE_COGNITO_DOMAIN ?? ''),
  redirectSignIn: import.meta.env.VITE_COGNITO_REDIRECT_SIGN_IN ?? `${browserOrigin()}/auth/callback`,
  redirectSignOut: import.meta.env.VITE_COGNITO_REDIRECT_SIGN_OUT ?? browserOrigin(),
  scopes: ['openid', 'email', 'profile'],
};

export const authConfigured = Boolean(authConfig.clientId && authConfig.domain);

export const loadSession = (): AuthSession | null => {
  if (!hasBrowserStorage()) return null;
  const raw = window.localStorage.getItem(sessionKey);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as AuthSession;
  } catch {
    window.localStorage.removeItem(sessionKey);
    return null;
  }
};

export const saveSession = (session: AuthSession) => {
  if (!hasBrowserStorage()) return;
  window.localStorage.setItem(sessionKey, JSON.stringify(session));
};

export const clearSession = () => {
  if (!hasBrowserStorage()) return;
  window.localStorage.removeItem(sessionKey);
  window.sessionStorage.removeItem(verifierKey);
  window.sessionStorage.removeItem(stateKey);
};

export const getUserProfile = (session: AuthSession | null): UserProfile | null => {
  if (!session?.idToken) return null;
  const claims = decodeJwtPayload(session.idToken);
  const groupsClaim = claims['cognito:groups'];
  const groups = Array.isArray(groupsClaim) ? groupsClaim.filter((group): group is string => typeof group === 'string') : [];

  return {
    email: typeof claims.email === 'string' ? claims.email : undefined,
    name: typeof claims.name === 'string' ? claims.name : undefined,
    username: typeof claims['cognito:username'] === 'string' ? claims['cognito:username'] : typeof claims.username === 'string' ? claims.username : undefined,
    groups,
  };
};

export const beginSignIn = async () => {
  ensureAuthConfigured();
  const verifier = randomUrlSafeString(64);
  const state = randomUrlSafeString(32);
  const challenge = await sha256Base64Url(verifier);

  window.sessionStorage.setItem(verifierKey, verifier);
  window.sessionStorage.setItem(stateKey, state);

  const params = new URLSearchParams({
    client_id: authConfig.clientId,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    redirect_uri: authConfig.redirectSignIn,
    response_type: 'code',
    scope: authConfig.scopes.join(' '),
    state,
  });

  window.location.assign(`${authConfig.domain}/oauth2/authorize?${params.toString()}`);
};

export const completeSignIn = async (code: string, state: string): Promise<AuthSession> => {
  ensureAuthConfigured();
  const expectedState = window.sessionStorage.getItem(stateKey);
  const verifier = window.sessionStorage.getItem(verifierKey);
  window.sessionStorage.removeItem(stateKey);
  window.sessionStorage.removeItem(verifierKey);

  if (!expectedState || expectedState !== state || !verifier) {
    throw new Error('The sign-in response could not be verified.');
  }

  const tokens = await tokenRequest({
    client_id: authConfig.clientId,
    code,
    code_verifier: verifier,
    grant_type: 'authorization_code',
    redirect_uri: authConfig.redirectSignIn,
  });

  const session = tokensToSession(tokens);
  saveSession(session);
  return session;
};

export const getValidAccessToken = async (): Promise<string | null> => {
  const session = loadSession();
  if (!session) return null;
  if (session.expiresAt > Date.now() + 60_000) return session.accessToken;
  if (!session.refreshToken) return null;

  try {
    const refreshed = await refreshSession(session.refreshToken);
    return refreshed.accessToken;
  } catch {
    clearSession();
    return null;
  }
};

export const refreshSession = async (refreshToken: string): Promise<AuthSession> => {
  ensureAuthConfigured();
  const current = loadSession();
  const tokens = await tokenRequest({
    client_id: authConfig.clientId,
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
  });

  const session = tokensToSession({
    ...tokens,
    id_token: tokens.id_token ?? current?.idToken,
    refresh_token: tokens.refresh_token ?? refreshToken,
  }, current);
  saveSession(session);
  return session;
};

export const signOut = () => {
  clearSession();
  if (!authConfigured) return;

  const params = new URLSearchParams({
    client_id: authConfig.clientId,
    logout_uri: authConfig.redirectSignOut,
  });
  window.location.assign(`${authConfig.domain}/logout?${params.toString()}`);
};

const tokenRequest = async (body: Record<string, string>): Promise<TokenResponse> => {
  const response = await fetch(`${authConfig.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams(body),
  });

  if (!response.ok) throw new Error('Cognito token exchange failed.');
  return (await response.json()) as TokenResponse;
};

const tokensToSession = (tokens: TokenResponse, previous?: AuthSession | null): AuthSession => ({
  accessToken: tokens.access_token,
  idToken: tokens.id_token,
  refreshToken: tokens.refresh_token,
  expiresAt: Date.now() + tokens.expires_in * 1000,
  createdAt: previous?.createdAt ?? Date.now(),
});

const ensureAuthConfigured = () => {
  if (!authConfigured) throw new Error('Cognito authentication is not configured.');
};

function normalizeDomain(domain: string): string {
  if (!domain) return '';
  const trimmed = domain.replace(/\/$/, '');
  return trimmed.startsWith('http://') || trimmed.startsWith('https://') ? trimmed : `https://${trimmed}`;
}

function browserOrigin(): string {
  return typeof window === 'undefined' ? '' : window.location.origin;
}

function hasBrowserStorage(): boolean {
  return typeof window !== 'undefined' && Boolean(window.localStorage && window.sessionStorage);
}

function randomUrlSafeString(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  window.crypto.getRandomValues(bytes);
  return base64Url(bytes);
}

async function sha256Base64Url(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value);
  const digest = await window.crypto.subtle.digest('SHA-256', bytes);
  return base64Url(new Uint8Array(digest));
}

function base64Url(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const [, payload] = token.split('.');
  if (!payload) return {};

  try {
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
    return JSON.parse(window.atob(padded)) as Record<string, unknown>;
  } catch {
    return {};
  }
}
